import {
  ARRIVAL_PACE,
  ARRIVAL_REDUCED_EXIT_MS,
  type ArrivalBeat,
  type ArrivalEasing,
} from '@/lib/arrival/arrival-schedule'

/** Every beat of the handover, in the order the founder sees them. */
export interface HandoverSchedule {
  /** The setup screen blurs away in place. */
  readonly screen: ArrivalBeat
  /** One beat per setup step row leaving the side nav, top to bottom; each rail leaves with the row above it. */
  readonly steps: readonly ArrivalBeat[]
  /** One beat per side-nav group header and item, top to bottom. */
  readonly navRows: readonly ArrivalBeat[]
  /** One beat per part of Home's content, in reading order. */
  readonly content: readonly ArrivalBeat[]
  /** The Create button blur-fades in. */
  readonly create: ArrivalBeat
  /** The tour hub at the foot of the side nav. */
  readonly hub: ArrivalBeat
  /** The floating chat button. */
  readonly dockButton: ArrivalBeat
  /** The beam of light running one lap of the content tile edge. */
  readonly beam: ArrivalBeat
  /** The whole tile edge glowing once and settling. */
  readonly edgeGlow: ArrivalBeat
  /** When the last beat ends. */
  readonly end: number
}

/** What the shell hands over to: how many step rows leave, how many nav rows arrive and how many content parts Home marks. */
export interface HandoverCast {
  readonly stepRows: number
  readonly navRows: number
  readonly contentParts: number
}

const DESIGNED_MS = {
  screen: 560,
  stepsDelay: 120,
  stepStagger: 80,
  step: 560,
  navRowsDelay: 600,
  navRowStagger: 45,
  navRow: 800,
  contentDelay: 700,
  contentStagger: 80,
  content: 900,
  createDelay: 1600,
  create: 750,
  hubDelay: 1870,
  hub: 600,
  dockButtonDelay: 2130,
  dockButton: 540,
  beamDelay: 300,
  beam: 1500,
  edgeGlowDelay: 1650,
  edgeGlow: 1000,
} as const

/** How long the leaving parts fade out under reduced motion; the same as the arrival's. */
export const HANDOVER_REDUCED_MS = ARRIVAL_REDUCED_EXIT_MS

function paced(designed: number): number {
  return Math.round(designed / ARRIVAL_PACE)
}

function beat(designedDelay: number, designedDuration: number, easing: ArrivalEasing): ArrivalBeat {
  return { delay: paced(designedDelay), duration: paced(designedDuration), easing }
}

function endOf(entry: ArrivalBeat): number {
  return entry.delay + entry.duration
}

/** Times every beat of the handover: setup screen leaves, step rows lift away, nav rows and Home content arrive, then Create, tour hub and chat button; the beam runs one lap while content arrives. */
export function handoverSchedule(cast: HandoverCast): HandoverSchedule {
  const steps = Array.from({ length: cast.stepRows }, (_, index) =>
    beat(DESIGNED_MS.stepsDelay + index * DESIGNED_MS.stepStagger, DESIGNED_MS.step, 'soft-exit'),
  )

  const navRows = Array.from({ length: cast.navRows }, (_, index) =>
    beat(DESIGNED_MS.navRowsDelay + index * DESIGNED_MS.navRowStagger, DESIGNED_MS.navRow, 'soft'),
  )

  const content = Array.from({ length: cast.contentParts }, (_, index) =>
    beat(
      DESIGNED_MS.contentDelay + index * DESIGNED_MS.contentStagger,
      DESIGNED_MS.content,
      'soft',
    ),
  )

  const screen = beat(0, DESIGNED_MS.screen, 'soft-exit')
  const create = beat(DESIGNED_MS.createDelay, DESIGNED_MS.create, 'soft')
  const hub = beat(DESIGNED_MS.hubDelay, DESIGNED_MS.hub, 'soft')
  const dockButton = beat(DESIGNED_MS.dockButtonDelay, DESIGNED_MS.dockButton, 'soft')
  const beam = beat(DESIGNED_MS.beamDelay, DESIGNED_MS.beam, 'standard')
  const edgeGlow = beat(DESIGNED_MS.edgeGlowDelay, DESIGNED_MS.edgeGlow, 'standard')

  const every = [screen, ...steps, ...navRows, ...content, create, hub, dockButton, beam, edgeGlow]

  return {
    screen,
    steps,
    navRows,
    content,
    create,
    hub,
    dockButton,
    beam,
    edgeGlow,
    end: Math.max(...every.map(endOf)),
  }
}
